import { useEffect, useState, useCallback } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const CATS = [
  { key: "clinic", l: "c_clinic" },
  { key: "doctor_comp", l: "c_doctor_comp" },
  { key: "internal_lab", l: "c_internal_lab" },
  { key: "external_lab", l: "c_external_lab" },
  { key: "consolidated", l: "c_consolidated" },
];
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const eur = (n) => `€${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function download(url, filename) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
    URL.revokeObjectURL(href);
  } catch { toast.error("Export failed"); }
}

export default function Accounting() {
  const t = useT();
  const [tab, setTab] = useState("clinic");
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = `start=${start}&end=${end}`;
  const load = useCallback(() => {
    setLoading(true);
    const url = tab === "consolidated" ? `/accounting/consolidated?${qs}` : `/accounting/ledger?category=${tab}&${qs}`;
    api.get(url).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [tab, qs]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("accounting.title")}
        subtitle={t("accounting.subtitle")} />
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><label className="text-xs text-slate-500">{t("accounting.from")}</label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="acct-start" /></div>
        <div><label className="text-xs text-slate-500">{t("accounting.to")}</label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="acct-end" /></div>
        <Button variant="outline" className="h-9" onClick={load} data-testid="acct-apply">{t("accounting.apply")}</Button>
        {tab !== "consolidated" && (
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" className="h-9" data-testid="acct-export-csv"
              onClick={() => download(`/accounting/ledger/export?category=${tab}&${qs}&format=csv`, `${tab}_${start}_${end}.csv`)}>
              <Download className="h-4 w-4 mr-1" /> CSV</Button>
            <Button variant="outline" className="h-9" data-testid="acct-export-pdf"
              onClick={() => download(`/accounting/ledger/export?category=${tab}&${qs}&format=pdf`, `${tab}_${start}_${end}.pdf`)}>
              <FileText className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="acct-tabs">
          {CATS.map((c) => <TabsTrigger key={c.key} value={c.key} data-testid={`acct-tab-${c.key}`}>{t(`accounting.${c.l}`)}</TabsTrigger>)}
        </TabsList>
        {CATS.map((c) => (
          <TabsContent key={c.key} value={c.key}>
            {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
              : c.key === "consolidated" ? <Consolidated data={data} t={t} /> : <Ledger data={data} t={t} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Ledger({ data, t }) {
  if (!data) return null;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="acct-total-qty">
          <p className="text-xs text-slate-500">{t("accounting.verifiedUnits")}</p>
          <p className="text-2xl font-heading text-slate-900">{data.total_quantity}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="acct-total-eur">
          <p className="text-xs text-slate-500">{data.category_label} {t("accounting.total")}</p>
          <p className="text-2xl font-heading text-navy">{eur(data.total_eur)}</p></div>
      </div>
      {Object.keys(data.by_item || {}).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(data.by_item).map(([k, v]) => (
            <span key={k} className="text-xs bg-slate-100 rounded-full px-3 py-1 text-slate-600">
              {v.item_label}: <b>{v.quantity}</b> · {eur(v.amount_eur)}</span>
          ))}
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="acct-ledger-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="px-4 py-2 font-medium">{t("accounting.date")}</th><th className="px-4 py-2 font-medium">{t("accounting.patient")}</th>
              <th className="px-4 py-2 font-medium">{t("accounting.item")}</th><th className="px-4 py-2 font-medium">{t("accounting.qty")}</th>
              <th className="px-4 py-2 font-medium">{t("accounting.rate")}</th><th className="px-4 py-2 font-medium">{t("accounting.amount")}</th>
              <th className="px-4 py-2 font-medium">{t("accounting.providerLab")}</th></tr>
          </thead>
          <tbody>
            {(data.entries || []).length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{t("accounting.noEntries")}</td></tr>}
            {(data.entries || []).map((e, i) => (
              <tr key={i} className="border-t border-slate-50">
                <td className="px-4 py-2 text-slate-500">{(e.date || "").slice(0, 10)}</td>
                <td className="px-4 py-2">{e.patient_name || "—"}</td>
                <td className="px-4 py-2">{e.item_label}</td>
                <td className="px-4 py-2 tabular-nums">{e.quantity}</td>
                <td className="px-4 py-2 tabular-nums">{eur(e.rate)}</td>
                <td className="px-4 py-2 tabular-nums font-medium">{eur(e.amount_eur)}</td>
                <td className="px-4 py-2 text-slate-500">{e.provider_name || "—"}{e.order_number ? ` · ${e.order_number}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Consolidated({ data, t }) {
  if (!data) return null;
  const s = data.summary;
  const cats = data.categories;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="acct-consolidated-summary">
        <Card label={t("accounting.clinicRevenue")} value={eur(s.clinic_revenue_eur)} tone="text-navy" />
        <Card label={t("accounting.doctorCompensation")} value={eur(s.doctor_compensation_eur)} />
        <Card label={t("accounting.internalLab")} value={eur(s.internal_lab_eur)} />
        <Card label={t("accounting.externalLab")} value={eur(s.external_lab_eur)} />
        <Card label={t("accounting.totalCost")} value={eur(s.total_cost_eur)} tone="text-red-600" />
        <Card label={t("accounting.grossMargin")} value={eur(s.gross_margin_eur)} tone="text-emerald-600" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(cats).map(([k, c]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`acct-cat-${k}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-slate-900">{c.category_label}</h3>
              <span className="text-sm text-slate-500">{c.total_quantity} {t("accounting.units")} · <b className="text-slate-900">{eur(c.total_eur)}</b></span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(c.by_item || {}).map(([ik, v]) => (
                <span key={ik} className="text-xs bg-slate-50 border border-slate-100 rounded px-2 py-0.5 text-slate-600">{v.item_label}: {v.quantity} · {eur(v.amount_eur)}</span>
              ))}
              {Object.keys(c.by_item || {}).length === 0 && <span className="text-xs text-slate-400">{t("accounting.noEntriesShort")}</span>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Card({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-heading ${tone}`}>{value}</p>
    </div>
  );
}
